/**
 * Directorio de cuentas. Alta y ficha en ventana.  (061)
 */
import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router'

import { useSesion } from '../../auth'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { api } from '../../lib/api'
import { CuerpoUsuarioFicha } from './usuario-ficha'
import { CuerpoUsuarioNuevo } from './usuario-nuevo'
import {
    cartelNombre,
    DialogoUsuario,
    estadoDe,
    type NegocioDir,
    type UsuarioDir,
} from './usuario-piezas'

interface Lista {
    usuarios: UsuarioDir[]
    negocios: NegocioDir[]
}

type Filtro = 'todos' | 'activos' | 'invitados' | 'deshabilitados'

export function UsuariosAdmin() {
    const { data: sesion } = useSesion()
    const nav = useNavigate()
    const loc = useLocation()
    const { id } = useParams()
    const [filtro, setFiltro] = useState('')
    const [vista, setVista] = useState<Filtro>('todos')
    const puede = sesion?.gestionaUsuarios === true

    const esNuevo = loc.pathname.endsWith('/nuevo')
    const fichaId = !esNuevo && id ? Number(id) : null

    const q = useQuery<Lista>({
        queryKey: ['usuarios'],
        queryFn: () => api.get<Lista>('/usuarios'),
        enabled: puede,
    })

    const negocios = q.data?.negocios ?? []
    const usuarios = q.data?.usuarios ?? []
    const ficha = fichaId ? usuarios.find((u) => u.id === fichaId) : undefined

    const visibles = useMemo(() => {
        const qf = filtro.trim().toLowerCase()
        return usuarios.filter((u) => {
            if (vista === 'activos' && (u.status !== 'active' || !u.tieneClave)) return false
            if (vista === 'invitados' && (u.status !== 'active' || u.tieneClave)) return false
            if (vista === 'deshabilitados' && u.status !== 'revoked') return false
            if (!qf) return true
            const nombre = cartelNombre(u).toLowerCase()
            return (
                nombre.includes(qf) ||
                u.email.includes(qf) ||
                u.accountIds.some((aid) =>
                    (negocios.find((n) => n.accountId === aid)?.name ?? '')
                        .toLowerCase()
                        .includes(qf),
                )
            )
        })
    }, [filtro, vista, usuarios, negocios])

    if (sesion && !puede) {
        return <p className="text-sm text-neutral-500">No encontrado</p>
    }

    function nombresDe(ids: number[]): string {
        if (ids.length === 0) return 'Ningún negocio'
        return ids
            .map((aid) => negocios.find((n) => n.accountId === aid)?.name ?? `#${aid}`)
            .join(', ')
    }

    function cerrar() {
        nav('/usuarios')
    }

    const filtros: { id: Filtro; etiqueta: string }[] = [
        { id: 'todos', etiqueta: 'Todos' },
        { id: 'activos', etiqueta: 'Activos' },
        { id: 'invitados', etiqueta: 'Invitados' },
        { id: 'deshabilitados', etiqueta: 'Deshabilitados' },
    ]

    return (
        <div className="space-y-5 font-sans" data-testid="usuarios-admin">
            <header className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="text-lg font-semibold text-neutral-100">Usuarios</h1>
                    <p className="mt-1 text-sm text-neutral-400">
                        Quién entra al panel y qué ve.
                    </p>
                </div>
                <Button type="button" onClick={() => nav('/usuarios/nuevo')}>
                    Nuevo usuario
                </Button>
            </header>

            <div className="flex flex-wrap gap-1">
                {filtros.map((f) => (
                    <button
                        key={f.id}
                        type="button"
                        onClick={() => setVista(f.id)}
                        className={
                            vista === f.id
                                ? 'rounded-md bg-neutral-100 px-2.5 py-1 text-xs text-neutral-900 light:bg-sky-500 light:text-white'
                                : 'rounded-md px-2.5 py-1 text-xs text-neutral-400 hover:bg-neutral-800'
                        }
                    >
                        {f.etiqueta}
                    </button>
                ))}
            </div>
            <div className="space-y-1.5">
                <Input
                    type="search"
                    name="filtro-listado"
                    id="filtro-listado"
                    autoComplete="off"
                    enterKeyHint="search"
                    role="searchbox"
                    aria-label="Filtrar el listado"
                    placeholder="Filtrar el listado"
                    value={filtro}
                    onChange={(e) => setFiltro(e.target.value)}
                />
                <p className="text-xs text-neutral-500">Por nombre, correo o negocio.</p>
            </div>

            {q.isPending && <p className="text-sm text-neutral-500">Cargando…</p>}
            {q.isError && <p className="text-sm text-rose-400">No se pudo leer.</p>}
            {q.data && visibles.length === 0 && (
                <p className="text-sm text-neutral-500">Ningún usuario.</p>
            )}

            {visibles.length > 0 && (
                <div className="overflow-x-auto rounded-lg border border-neutral-800">
                    <table className="tabla-carta w-full border-collapse text-sm">
                        <thead>
                            <tr className="border-b border-neutral-800 text-left text-xs text-neutral-500">
                                <th className="px-3 py-2 font-medium">Persona</th>
                                <th className="px-3 py-2 font-medium">Estado</th>
                                <th className="px-3 py-2 font-medium">Acceso</th>
                                <th className="px-3 py-2 font-medium">Negocios</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visibles.map((u) => {
                                const est = estadoDe(u)
                                return (
                                    <tr
                                        key={u.id}
                                        data-testid="fila-usuario"
                                        role="button"
                                        tabIndex={0}
                                        aria-label={`Abrir ${cartelNombre(u)}`}
                                        onClick={() => nav(`/usuarios/${u.id}`)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault()
                                                nav(`/usuarios/${u.id}`)
                                            }
                                        }}
                                        className="cursor-pointer border-b border-neutral-900 last:border-b-0 hover:bg-neutral-800/50 light:hover:bg-neutral-200/70"
                                    >
                                        <td className="px-3 py-3" data-label="Persona">
                                            <span className="block font-medium text-neutral-100">
                                                {cartelNombre(u)}
                                            </span>
                                            <span className="block text-xs text-neutral-500">
                                                {u.email}
                                            </span>
                                        </td>
                                        <td className="px-3 py-3" data-label="Estado">
                                            <Badge tone={est.tone}>{est.texto}</Badge>
                                        </td>
                                        <td className="px-3 py-3" data-label="Acceso">
                                            {u.soloLectura ? (
                                                <Badge tone="warning">Solo lectura</Badge>
                                            ) : (
                                                <Badge tone="success">Puede escribir</Badge>
                                            )}
                                        </td>
                                        <td
                                            className="px-3 py-3 text-neutral-400"
                                            data-label="Negocios"
                                        >
                                            {nombresDe(u.accountIds)}
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {esNuevo && (
                <DialogoUsuario titulo="Nuevo usuario" onCerrar={cerrar}>
                    <CuerpoUsuarioNuevo negocios={negocios} onCerrar={cerrar} />
                </DialogoUsuario>
            )}
            {fichaId !== null && !q.isPending && !ficha && (
                <DialogoUsuario titulo="Usuario" onCerrar={cerrar}>
                    <p className="text-sm text-neutral-500">No encontrado</p>
                </DialogoUsuario>
            )}
            {ficha && (
                <DialogoUsuario titulo={cartelNombre(ficha)} onCerrar={cerrar}>
                    <CuerpoUsuarioFicha
                        usuario={ficha}
                        negocios={negocios}
                        onCerrar={cerrar}
                    />
                </DialogoUsuario>
            )}
        </div>
    )
}
